/*
 * Stay or Go? — calculation engine and configuration shape.
 *
 * Ported verbatim from the prototype (artifacts/prototypes/vendor-exit-diagnostic-v1.html).
 * The math has been tuned through user feedback — do NOT redesign it. Constants
 * default to the prototype's values; admin can override via published config.
 *
 * Input shape lines up with what the .NET payload validator (StayOrGoV1Validator)
 * expects: top-level `team`, `tier`, and `answers` map.
 *
 * Output `computed.*` field names are camelCase to match the SQL projection
 * view (vw_StayOrGoSubmissions) which JSON_VALUEs them out by exact path.
 */

// ─── Config shapes ──────────────────────────────────────────────────────────

export interface StatItem {
  num: string;
  label: string;
}

export interface TeamSize {
  /** Stable key — must match the validator enum (micro|small|mid|large|enterprise). */
  key: 'micro' | 'small' | 'mid' | 'large' | 'enterprise' | string;
  label: string;
  sub: string;
  median: number;
}

export interface SpendTier {
  /** Stable key — must match validator enum (small|mid|large|enterprise). */
  key: 'small' | 'mid' | 'large' | 'enterprise' | string;
  label: string;
  sub: string;
  annual: number;
}

/** Per-question option override. Only used by the user-adoption question by default. */
export interface QuestionOption {
  key: string;
  label: string;
  /** UI tone: drives the selected-state color. */
  tone: 'yes' | 'no' | 'unsure';
}

export interface Question {
  id: string;
  /** Stable analytics tag — referenced by the mirror copy. */
  tag: string;
  /** Question text. */
  q: string;
  /** Embedded stat (HTML allowed in the prototype). */
  stat: string;
  /** Optional per-question options. Default = yes/no/unsure. */
  options?: QuestionOption[];
  /** Map of answer key -> Stay Score delta. */
  weight: Record<string, number>;
}

export interface VerdictCopy {
  label: string;
  body: string;
}

export interface Verdicts {
  leave: VerdictCopy;
  mid: VerdictCopy;
  stay: VerdictCopy;
}

export interface Thresholds {
  leave: number;
  mid: number;
}

export interface CalcConfig {
  priceEscalation: { peOwned: number; ownershipChange: number; baseline: number };
  perFteAnnualBase: number;
  perFteWorkaroundBoost: number;
  perFteRigidityBoost: number;
  productivityDragPct: number;
  hourlyCost: number;
  hoursPerMonth: number;
  switchImplPctOfAnnual: number;
  switchParallelMonths: number;
  switchInternalPerFte: number;
  switchProdDipPct: number;
  switchProdDipMonths: number;
  newVendorEfficiency: number;
}

export interface ContentCopy {
  navTag: string;
  heroBadge: string;
  heroTitle: string;
  heroSub: string;
  heroMeta: string;
}

export interface StayOrGoConfig {
  content: ContentCopy;
  stats: StatItem[];
  questions: Question[];
  thresholds: Thresholds;
  verdicts: Verdicts;
  calc: CalcConfig;
  spendTiers: SpendTier[];
  teamSizes: TeamSize[];
}

// ─── Defaults (ported from the prototype) ───────────────────────────────────

export const DEFAULT_CONTENT: ContentCopy = {
  navTag: 'Stay or Go?',
  heroBadge: 'Stay or Go? · 4-minute diagnostic',
  heroTitle:
    'Is your legal tech still <em>delivering</em> — or just extracting money?',
  heroSub:
    "A short diagnostic for legal teams already living with a vendor that hasn't lived up to its pitch. The data does the talking. You'll know within 4 minutes whether it's worth fighting for — and what staying is costing you.",
  heroMeta: 'No account. No email gate. Stays in your browser.',
};

export const DEFAULT_STATS: StatItem[] = [
  { num: '77%', label: "of legal tech projects don't deliver what was promised" },
  {
    num: '27%',
    label: 'of buyers who lived through a failure lost confidence in themselves',
  },
  {
    num: '$4.3B',
    label:
      'legal tech funding in 2025 alone — up 54% YoY, extraction pressure intensifying',
  },
  { num: '3–5×', label: 'typical multiple legal teams pay over their initial budget' },
];

export const DEFAULT_TEAM_SIZES: TeamSize[] = [
  { key: 'mid', label: '16–25', sub: 'Mid-sized', median: 20 },
  { key: 'large', label: '26–50', sub: 'Large dept.', median: 38 },
  { key: 'enterprise', label: '50+', sub: 'Enterprise', median: 100 },
];

export const DEFAULT_SPEND_TIERS: SpendTier[] = [
  { key: 'small', label: 'Small', sub: '<$50K/yr', annual: 50000 },
  { key: 'mid', label: 'Mid', sub: '$50K–$250K/yr', annual: 175000 },
  { key: 'large', label: 'Large', sub: '$250K–$1M/yr', annual: 500000 },
  { key: 'enterprise', label: 'Enterprise', sub: '>$1M/yr', annual: 1500000 },
];

export const DEFAULT_QUESTIONS: Question[] = [
  {
    id: 'ownershipChange',
    tag: 'ownership',
    q: "Has your vendor's ownership changed in the last 36 months?",
    stat: "PE/VC firms time their plays around <strong>5–7 year exit windows</strong>. If ownership changed, you're now in someone else's strategy — not the one you bought.",
    weight: { yes: -2, no: 1, unsure: 0 },
  },
  {
    id: 'priceClimb',
    tag: 'pricing',
    q: 'Has your renewal pricing climbed faster than 5%/yr?',
    stat: "PE-owned vendors typically target <strong>10–15%+ price escalation</strong> per cycle. If you're seeing that, it's not negotiation — it's extraction phase 4.",
    weight: { yes: -2, no: 1, unsure: 0 },
  },
  {
    id: 'csmTurnover',
    tag: 'support',
    q: 'Has your dedicated CSM (or account team) turned over twice or more?',
    stat: 'Support consolidation is <strong>phase 3 of the extraction playbook</strong>. Pool support is cheaper for the vendor — and typically slower for you.',
    weight: { yes: -2, no: 1, unsure: 0 },
  },
  {
    id: 'workarounds',
    tag: 'workarounds',
    q: "Is your team maintaining 5+ active workarounds for things the system can't handle?",
    stat: 'A mid-sized legal team typically pays <strong>$100K–$200K per year</strong> in workaround maintenance and routine reconfiguration. Each new workaround compounds.',
    weight: { yes: -2, no: 1, unsure: 0 },
  },
  {
    id: 'roadmap',
    tag: 'roadmap',
    q: 'Has the product roadmap they sold you actually shipped on time?',
    stat: '<strong>Missed roadmap deadlines</strong> are a leading indicator that engineering investment is being redirected — usually toward the next acquisition or cost-cutting.',
    weight: { yes: 2, no: -2, unsure: 0 },
  },
  {
    id: 'userAdoption',
    tag: 'adoption',
    q: 'What percentage of expected users have fully adopted and continue to use the solution?',
    stat: '<strong>Adoption decay is the strongest predictor of failed value</strong>. A solution measured by usage tells you whether you bought a tool — or a license your team is avoiding.',
    options: [
      { key: 'high', label: '>80%', tone: 'no' },
      { key: 'mid', label: '50–80%', tone: 'unsure' },
      { key: 'low', label: '<50%', tone: 'yes' },
      { key: 'unsure', label: "Don't know", tone: 'unsure' },
    ],
    weight: { high: 2, mid: 0, low: -2, unsure: -1 },
  },
  {
    id: 'rigidity',
    tag: 'rigidity',
    q: 'Can you add a new workgroup, jurisdiction, or use case in under 90 days without vendor professional services?',
    stat: "When every change becomes a project, your <strong>adaptation tax compounds</strong>. The cost of rigidity is rarely on the invoice — it's in the time and headcount it absorbs.",
    weight: { yes: 2, no: -2, unsure: 0 },
  },
  {
    id: 'wouldRebuy',
    tag: 'rebuy',
    q: 'If you started fresh today, would you still pick this vendor?',
    stat: 'The cleanest signal there is. <strong>23%</strong> of buyers who lived through a failed implementation actually changed jobs over it. A "no" here is the answer; the rest is just math.',
    weight: { yes: 2, no: -3, unsure: -1 },
  },
];

export const DEFAULT_THRESHOLDS: Thresholds = { leave: 3, mid: 6 };

export const DEFAULT_VERDICTS: Verdicts = {
  leave: {
    label: 'Time to move on',
    body: "The pattern you're experiencing is structural, not personal. The math has already shifted under you — the longer you stay, the more it compounds.",
  },
  mid: {
    label: 'Time to renegotiate or replace',
    body: "You're past the easy phase. Some of what you're seeing is fixable; some of it isn't. The next 6 months should be a forced choice — fight for what you have, or start your evaluation.",
  },
  stay: {
    label: 'Worth investing in further',
    body: 'The fundamentals are still in your favor. The work now is operational — getting more value out of what you have, not replacing it.',
  },
};

export const DEFAULT_CALC: CalcConfig = {
  priceEscalation: { peOwned: 1.25, ownershipChange: 1.15, baseline: 1.05 },
  perFteAnnualBase: 8500,
  perFteWorkaroundBoost: 2500,
  perFteRigidityBoost: 3500,
  productivityDragPct: 0.15,
  hourlyCost: 150,
  hoursPerMonth: 160,
  switchImplPctOfAnnual: 0.5,
  switchParallelMonths: 4,
  switchInternalPerFte: 5000,
  switchProdDipPct: 0.2,
  switchProdDipMonths: 4,
  newVendorEfficiency: 0.4,
};

export const DEFAULT_CONFIG: StayOrGoConfig = {
  content: DEFAULT_CONTENT,
  stats: DEFAULT_STATS,
  questions: DEFAULT_QUESTIONS,
  thresholds: DEFAULT_THRESHOLDS,
  verdicts: DEFAULT_VERDICTS,
  calc: DEFAULT_CALC,
  spendTiers: DEFAULT_SPEND_TIERS,
  teamSizes: DEFAULT_TEAM_SIZES,
};

// ─── Runtime state ──────────────────────────────────────────────────────────

export interface ToolState {
  team: string | null;
  tier: string | null;
  answers: Record<string, string>;
}

// ─── Calculations ───────────────────────────────────────────────────────────

/**
 * Stay Score 0–10. Starts at 5, weighted answers move it. Linear-mapped
 * back into [0,10] using the actual min/max possible from the question set.
 */
export function calcStayScore(state: ToolState, questions: Question[]): number {
  let s = 5;
  questions.forEach((q) => {
    const ans = state.answers[q.id];
    if (!ans) return;
    s += q.weight[ans] || 0;
  });
  let bestSum = 0;
  let worstSum = 0;
  questions.forEach((q) => {
    const values = Object.values(q.weight);
    bestSum += Math.max(...values);
    worstSum += Math.min(...values);
  });
  const maxPossible = 5 + bestSum;
  const minPossible = 5 + worstSum;
  if (maxPossible === minPossible) return 5; // degenerate config
  const normalized = ((s - minPossible) / (maxPossible - minPossible)) * 10;
  return Math.max(0, Math.min(10, Math.round(normalized * 10) / 10));
}

export type VerdictTone = 'warn' | 'mid' | 'good';

export interface Verdict {
  label: string;
  body: string;
  tone: VerdictTone;
  short: string;
}

export function verdictFor(score: number, t: Thresholds, v: Verdicts): Verdict {
  if (score <= t.leave) return { ...v.leave, tone: 'warn', short: 'leave signal' };
  if (score <= t.mid) return { ...v.mid, tone: 'mid', short: 'mixed signals' };
  return { ...v.stay, tone: 'good', short: 'stay signal' };
}

export interface CostOfStaying {
  subscription24mo: number;
  adaptation24mo: number;
  adoption24mo: number;
  total: number;
  /** Escalation rate as a fraction (0.05 = 5%). */
  priceEscalation: number;
}

export function calcCost(
  state: ToolState,
  cfg: CalcConfig,
  teamSizes: TeamSize[],
  spendTiers: SpendTier[],
): CostOfStaying | null {
  if (!state.team || !state.tier) return null;
  const team = teamSizes.find((t) => t.key === state.team);
  const tier = spendTiers.find((t) => t.key === state.tier);
  if (!team || !tier) return null;
  const ans = state.answers;

  let priceEscalation = cfg.priceEscalation.baseline;
  if (ans.priceClimb === 'yes') priceEscalation = cfg.priceEscalation.peOwned;
  else if (ans.ownershipChange === 'yes')
    priceEscalation = cfg.priceEscalation.ownershipChange;
  const subscription24mo = tier.annual + tier.annual * priceEscalation;

  let perFteAnnual = cfg.perFteAnnualBase;
  if (ans.workarounds === 'yes') perFteAnnual += cfg.perFteWorkaroundBoost;
  if (ans.rigidity === 'no') perFteAnnual += cfg.perFteRigidityBoost;
  const adaptationAnnual = perFteAnnual * team.median;
  const adaptation24mo = adaptationAnnual * 2;

  let dragMult = 0;
  if (ans.userAdoption === 'low') dragMult = 1;
  else if (ans.userAdoption === 'mid') dragMult = 0.5;
  else if (ans.userAdoption === 'unsure') dragMult = 0.5;
  const adoption24mo =
    dragMult *
    cfg.productivityDragPct *
    team.median *
    cfg.hourlyCost *
    cfg.hoursPerMonth *
    24;

  const total = subscription24mo + adaptation24mo + adoption24mo;
  return {
    subscription24mo,
    adaptation24mo,
    adoption24mo,
    total,
    priceEscalation: priceEscalation - 1,
  };
}

export interface CostOfSwitching {
  implementation: number;
  parallelRun: number;
  internalTime: number;
  productivityDip: number;
  total: number;
}

export function calcSwitchCost(
  state: ToolState,
  cfg: CalcConfig,
  teamSizes: TeamSize[],
  spendTiers: SpendTier[],
): CostOfSwitching | null {
  if (!state.team || !state.tier) return null;
  const team = teamSizes.find((t) => t.key === state.team);
  const tier = spendTiers.find((t) => t.key === state.tier);
  if (!team || !tier) return null;
  const implementation = Math.round(tier.annual * cfg.switchImplPctOfAnnual);
  const parallelRun = Math.round(tier.annual * (cfg.switchParallelMonths / 12));
  const internalTime = Math.round(cfg.switchInternalPerFte * team.median);
  const productivityDip = Math.round(
    cfg.switchProdDipPct *
      team.median *
      cfg.hourlyCost *
      cfg.hoursPerMonth *
      cfg.switchProdDipMonths,
  );
  const total = implementation + parallelRun + internalTime + productivityDip;
  return { implementation, parallelRun, internalTime, productivityDip, total };
}

export interface PaybackAndValue {
  monthlyStaying: number;
  monthlySavings: number;
  paybackMonths: number;
  threeYearValue: number;
}

export function calcPaybackAndValue(
  stayingCost: CostOfStaying | null,
  switchCost: CostOfSwitching | null,
  cfg: CalcConfig,
): PaybackAndValue | null {
  if (!stayingCost || !switchCost) return null;
  const monthlyStaying = stayingCost.total / 24;
  const efficiency = cfg.newVendorEfficiency;
  const monthlySavings = monthlyStaying * efficiency;
  if (monthlySavings <= 0) return null;
  const paybackMonths = Math.max(1, Math.round(switchCost.total / monthlySavings));
  const threeYearValue = Math.round(monthlySavings * 36 - switchCost.total);
  return { monthlyStaying, monthlySavings, paybackMonths, threeYearValue };
}

export function fmtMoney(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return '$' + Math.round(n).toLocaleString('en-US');
}

export function answeredCount(state: ToolState, questions: Question[]): number {
  return questions.filter((q) => state.answers[q.id]).length;
}

/**
 * Mirror copy — names what the buyer is experiencing in the framework.
 * Returns HTML (consumed by dangerouslySetInnerHTML in the results card).
 */
export function buildMirror(tagsHit: string[], score: number): string {
  const intro =
    score <= 3
      ? `<strong>You didn't pick wrong.</strong> The math changed under you. The vendor you bought from is not the same company today — that's the whole point of the playbook you're seeing in real time.`
      : score <= 6
      ? `<strong>The signals are mixed.</strong> What you're feeling is real, but it's not yet a clear leave. The next 6 months are the deciding window — either you renegotiate the relationship, or you start the evaluation in parallel so you have leverage at renewal.`
      : `<strong>The vendor is still earning its seat.</strong> The work now is operational — getting more value out of what you have. Watch the signals; some of them age fast.`;

  const observations: string[] = [];
  if (tagsHit.includes('ownership')) observations.push('the ownership change is rarely benign');
  if (tagsHit.includes('pricing'))
    observations.push(
      'pricing escalation is the most common <strong>extraction-phase 4</strong> tell',
    );
  if (tagsHit.includes('support'))
    observations.push(
      'CSM turnover is <strong>extraction-phase 3</strong> — support consolidation typically follows acquisition by 18–24 months',
    );
  if (tagsHit.includes('workarounds'))
    observations.push("the workaround load is the part of the cost that doesn't show up on the invoice");
  if (tagsHit.includes('roadmap'))
    observations.push(
      'roadmap delays after acquisition are textbook — engineering investment goes elsewhere',
    );
  if (tagsHit.includes('adoption'))
    observations.push('voluntary adoption is the strongest predictor of value loss');
  if (tagsHit.includes('rigidity'))
    observations.push(
      "the rigidity tax compounds — every workgroup or use case you can't add becomes a future migration",
    );
  if (tagsHit.includes('rebuy'))
    observations.push(
      'the "would I rebuy?" question is the cleanest signal there is — and you said no',
    );

  let pattern = '';
  if (observations.length >= 1) {
    pattern = ' Specifically: ' + observations.slice(0, 3).join('; ') + '.';
  }

  const close =
    score <= 3
      ? " Knowing this — naming it — is the muscle for the next decision. You don't have to leave today. You do have to stop pretending the relationship will fix itself."
      : score <= 6
      ? " Either path is honest. The one you can't afford is the third one — pretending nothing is wrong while the cost compounds."
      : ' The win here is operational discipline. Audit your unused features, push for a roadmap commitment in writing, lock pricing for 24 months while leverage is on your side.';

  return intro + pattern + close;
}

// ─── Submission shape ──────────────────────────────────────────────────────

/**
 * Inner payload sent to the API. Top-level `team`/`tier`/`answers` match
 * what StayOrGoV1Validator.cs validates; `computed.*` field names are
 * camelCase to match the SQL projection view JSON paths.
 */
export interface StayOrGoPayload {
  team: string;
  tier: string;
  answers: Record<string, string>;
  computed: {
    score: number;
    verdictLabel: string;
    verdictTone: VerdictTone;
    costOfStaying24mo: number | null;
    subscriptionCost24mo: number | null;
    adaptationCost24mo: number | null;
    adoptionDrag24mo: number | null;
    costOfSwitching: number | null;
    priceEscalation: number | null;
  };
}

export function buildPayload(
  state: ToolState,
  config: StayOrGoConfig,
): StayOrGoPayload {
  const score = calcStayScore(state, config.questions);
  const verdict = verdictFor(score, config.thresholds, config.verdicts);
  const cost = calcCost(state, config.calc, config.teamSizes, config.spendTiers);
  const switchCost = calcSwitchCost(
    state,
    config.calc,
    config.teamSizes,
    config.spendTiers,
  );
  return {
    team: state.team!,
    tier: state.tier!,
    answers: { ...state.answers },
    computed: {
      score,
      verdictLabel: verdict.label,
      verdictTone: verdict.tone,
      costOfStaying24mo: cost ? Math.round(cost.total) : null,
      subscriptionCost24mo: cost ? Math.round(cost.subscription24mo) : null,
      adaptationCost24mo: cost ? Math.round(cost.adaptation24mo) : null,
      adoptionDrag24mo: cost ? Math.round(cost.adoption24mo) : null,
      costOfSwitching: switchCost ? Math.round(switchCost.total) : null,
      priceEscalation: cost ? cost.priceEscalation : null,
    },
  };
}
